// Client-side auth: session context, route guard, membership hook.
//
// No router in this app — pages auto-mount from src/pages/<name>.tsx, so navigation
// is plain `location` assignment. RequireAuth therefore redirects with
// location.replace() rather than a <Navigate>.
//
// AuthProvider is nest-safe: if one is already mounted above (spz-00 is expected to
// wrap the app root in main.tsx), an inner one passes straight through without
// opening a second auth subscription. That lets individual pages wrap themselves
// today and keeps working unchanged once the root provider lands.

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type JSX,
  type ReactNode,
} from 'react'
import type { Session, User } from '@supabase/supabase-js'
import { supabase } from './supabaseClient'
import type { Membership, Role } from './types'

export interface AuthState {
  session: Session | null
  user: User | null
  loading: boolean
}

const AuthContext = createContext<AuthState | null>(null)

export function AuthProvider(props: { children: ReactNode }): JSX.Element {
  const outer = useContext(AuthContext)
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (outer) return // an ancestor already owns the subscription
    let active = true

    // Subscribe BEFORE the initial read so a session arriving from the magic-link
    // URL hash mid-flight is not missed.
    const { data: sub } = supabase.auth.onAuthStateChange((_event, next) => {
      if (!active) return
      setSession(next)
      setLoading(false)
    })

    void supabase.auth.getSession().then(({ data }) => {
      if (!active) return
      setSession(data.session)
      setLoading(false)
    })

    return () => {
      active = false
      sub.subscription.unsubscribe()
    }
  }, [outer])

  const value = useMemo<AuthState>(
    () => ({ session, user: session?.user ?? null, loading }),
    [session, loading],
  )

  if (outer) return <>{props.children}</>
  return <AuthContext.Provider value={value}>{props.children}</AuthContext.Provider>
}

export function useAuth(): AuthState {
  return useContext(AuthContext) ?? { session: null, user: null, loading: true }
}

/**
 * The caller's own membership row for a trip, read through the anon client — RLS
 * lets a user see their own row. `null` while logged out or while there is no row.
 */
export function useMembership(tripId: string): { membership: Membership | null; loading: boolean } {
  const { user, loading: authLoading } = useAuth()
  const userId = user?.id ?? null
  const [membership, setMembership] = useState<Membership | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (authLoading) {
      setLoading(true)
      return
    }
    if (!userId || !tripId) {
      setMembership(null)
      setLoading(false)
      return
    }

    let active = true
    setLoading(true)

    void (async () => {
      const { data, error } = await supabase
        .from('membership')
        // Every column of Membership: a partial select would make the cast below
        // a lie (types.ts marks all of them required).
        .select('trip_id, user_id, household_id, role, created_at')
        .eq('trip_id', tripId)
        .eq('user_id', userId)
        .maybeSingle()

      if (!active) return
      setMembership(error || !data ? null : (data as unknown as Membership))
      setLoading(false)
    })()

    return () => {
      active = false
    }
  }, [tripId, userId, authLoading])

  return { membership, loading }
}

/** Convenience: is the caller an organizer on this trip? */
export function useIsOrganizer(tripId: string): boolean {
  const { membership } = useMembership(tripId)
  const role: Role | undefined = membership?.role
  return role === 'organizer'
}

/** Current access token, or null when logged out. Use it to build Authorization headers. */
export async function getAccessToken(): Promise<string | null> {
  const { data } = await supabase.auth.getSession()
  return data.session?.access_token ?? null
}

/** Where to send the user back to after a magic-link round trip. */
export function currentPathForNext(): string {
  return `${window.location.pathname}${window.location.search}`
}

/**
 * Supabase reports a dead magic link by appending `#error=…&error_description=…`
 * to the redirect URL. Read it off the hash so the page can offer a new link
 * instead of a blank screen.
 */
export function authErrorFromHash(): string | null {
  const hash = window.location.hash.replace(/^#/, '')
  if (!hash) return null
  const params = new URLSearchParams(hash)
  const description = params.get('error_description') ?? params.get('error')
  return description ? description.replace(/\+/g, ' ') : null
}

/** Renders children if logged in; otherwise redirects to /login?next=<current path+query>. */
export function RequireAuth(props: { children: ReactNode }): JSX.Element {
  const { session, loading } = useAuth()

  useEffect(() => {
    if (loading || session) return
    // Carry a dead-link error across the redirect — the error lands on the page the
    // magic link pointed at, but the "send a new link" button lives on /login.
    const authError = authErrorFromHash()
    const query = new URLSearchParams({ next: currentPathForNext() })
    if (authError) query.set('auth_error', authError)
    window.location.replace(`/login?${query.toString()}`)
  }, [loading, session])

  if (loading) {
    return <p style={{ padding: '2rem 1.25rem', color: 'var(--muted)' }}>Loading&hellip;</p>
  }
  if (!session) {
    return (
      <p style={{ padding: '2rem 1.25rem', color: 'var(--muted)' }}>Taking you to sign in&hellip;</p>
    )
  }
  return <>{props.children}</>
}
